<template>
  <div class="aw-tooltip" ref="tooltipContainer">
    <div @click="toggleTooltip" class="aw-tooltip-trigger">
      <slot name="trigger"></slot>
    </div>

    <Transition name="tooltip">
      <div v-if="isOpen" class="aw-tooltip-content" :class="currentPosition" ref="tooltipContent">
        <slot name="content"></slot>
      </div>
    </Transition>
  </div>
</template>

<script>
import { ref, onMounted, onBeforeUnmount } from 'vue'

const currentlyOpenTooltip = ref(null)

export default {
  name: 'AwTooltip',
  props: {
    position: {
      type: String,
      default: 'top',
      validator: value => ['top', 'bottom', 'left', 'right'].includes(value)
    }
  },
  setup(props) {
    const isOpen = ref(false)
    const tooltipContainer = ref(null)
    const tooltipContent = ref(null)
    const currentPosition = ref(props.position)

    const updatePosition = () => {
      if (!tooltipContainer.value || !isOpen.value) return

      const trigger = tooltipContainer.value.querySelector('.aw-tooltip-trigger')
      if (!trigger) return

      const triggerRect = trigger.getBoundingClientRect()
      const viewportHeight = window.innerHeight
      const viewportWidth = window.innerWidth
      const EDGE_GAP = 30 // gap from viewport edges
      const tooltipWidth = 300 // min-width of tooltip
      const tooltipHalfWidth = tooltipWidth / 2
      const triggerCenter = triggerRect.left + triggerRect.width / 2

      // Check vertical space first
      const spaceAbove = triggerRect.top
      const spaceBelow = viewportHeight - triggerRect.bottom
      const minSpace = 100 // minimum space needed for tooltip

      if (currentPosition.value === 'top' && spaceAbove < minSpace && spaceBelow > minSpace) {
        currentPosition.value = 'bottom'
      } else if (
        currentPosition.value === 'bottom' &&
        spaceBelow < minSpace &&
        spaceAbove > minSpace
      ) {
        currentPosition.value = 'top'
      }

      // Check horizontal space
      if (currentPosition.value === 'left' && triggerRect.left < tooltipWidth) {
        currentPosition.value = 'right'
      } else if (
        currentPosition.value === 'right' &&
        triggerRect.right > viewportWidth - tooltipWidth
      ) {
        currentPosition.value = 'left'
      }

      // Calculate horizontal offset for top/bottom positions
      if (currentPosition.value === 'top' || currentPosition.value === 'bottom') {
        let offset = 0
        if (triggerCenter - tooltipHalfWidth < EDGE_GAP) {
          // Tooltip would overflow left side
          offset = EDGE_GAP - (triggerCenter - tooltipHalfWidth)
        } else if (triggerCenter + tooltipHalfWidth > viewportWidth - EDGE_GAP) {
          // Tooltip would overflow right side
          offset = viewportWidth - EDGE_GAP - (triggerCenter + tooltipHalfWidth)
        }

        if (offset !== 0) {
          tooltipContent.value.style.transform = `translateX(calc(-50% + ${offset}px))`
          // Arrow offset needs to be relative to the trigger position
          tooltipContent.value.style.setProperty('--arrow-offset', `${-offset}px`)
        } else {
          tooltipContent.value.style.transform = 'translateX(-50%)'
          tooltipContent.value.style.setProperty('--arrow-offset', '0px')
        }
      }
    }

    const toggleTooltip = event => {
      event.stopPropagation()
      if (!isOpen.value) {
        if (currentlyOpenTooltip.value) {
          currentlyOpenTooltip.value.isOpen = false
        }
        currentlyOpenTooltip.value = { isOpen, container: tooltipContainer }
        currentPosition.value = props.position
        setTimeout(() => {
          updatePosition()
        }, 0)
      }
      isOpen.value = !isOpen.value
    }

    const handleClickOutside = event => {
      if (isOpen.value && !tooltipContainer.value.contains(event.target)) {
        isOpen.value = false
        if (currentlyOpenTooltip.value?.container === tooltipContainer.value) {
          currentlyOpenTooltip.value = null
        }
      }
    }

    let resizeObserver = null
    let scrollHandler = null

    onMounted(() => {
      document.addEventListener('click', handleClickOutside)

      resizeObserver = new ResizeObserver(() => {
        if (isOpen.value) {
          updatePosition()
        }
      })
      resizeObserver.observe(document.body)

      scrollHandler = () => {
        if (isOpen.value) {
          updatePosition()
        }
      }
      window.addEventListener('scroll', scrollHandler)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', handleClickOutside)
      if (resizeObserver) {
        resizeObserver.disconnect()
      }
      if (scrollHandler) {
        window.removeEventListener('scroll', scrollHandler)
      }
      if (currentlyOpenTooltip.value?.container === tooltipContainer.value) {
        currentlyOpenTooltip.value = null
      }
    })

    return {
      isOpen,
      tooltipContainer,
      tooltipContent,
      currentPosition,
      toggleTooltip,
      handleClickOutside
    }
  }
}
</script>

<style scoped>
.aw-tooltip {
  position: relative;
  display: inline-block;
}

.aw-tooltip-trigger {
  cursor: pointer;
  user-select: none;
}

.aw-tooltip-content {
  @apply bg-ambi-gray;
  position: absolute;
  z-index: 1000;
  border-radius: 4px;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15));
  padding: 12px 16px;
  min-width: 300px;
  max-width: 100vw;
}

.aw-tooltip-content.top {
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
}

.aw-tooltip-content.bottom {
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 8px;
}

.aw-tooltip-content.left {
  right: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-right: 8px;
}

.aw-tooltip-content.right {
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 8px;
}

.aw-tooltip-content::before {
  content: '';
  position: absolute;
  width: 0;
  height: 0;
  border: 8px solid transparent;
  filter: drop-shadow(0 2px 8px rgba(0, 0, 0, 0.15));
}

.aw-tooltip-content.top::before {
  bottom: -16px;
  left: 50%;
  transform: translateX(calc(-50% + var(--arrow-offset, 0px)));
  border-top-color: theme('colors.ambi-gray');
}

.aw-tooltip-content.bottom::before {
  top: -16px;
  left: 50%;
  transform: translateX(calc(-50% + var(--arrow-offset, 0px)));
  border-bottom-color: theme('colors.ambi-gray');
}

.aw-tooltip-content.left::before {
  right: -16px;
  top: 50%;
  transform: translateY(-50%);
  border-left-color: theme('colors.ambi-gray');
}

.aw-tooltip-content.right::before {
  left: -16px;
  top: 50%;
  transform: translateY(-50%);
  border-right-color: theme('colors.ambi-gray');
}

.tooltip-enter-active,
.tooltip-leave-active {
  transition: all 0.2s ease-out;
}

.tooltip-enter-from,
.tooltip-leave-to {
  opacity: 0;
}

.tooltip-enter-from.top,
.tooltip-leave-to.top {
  transform: translate(calc(-50% + var(--arrow-offset, 0px)), 8px);
}

.tooltip-enter-from.bottom,
.tooltip-leave-to.bottom {
  transform: translate(calc(-50% + var(--arrow-offset, 0px)), -8px);
}

.tooltip-enter-from.left,
.tooltip-leave-to.left {
  transform: translate(8px, -50%);
}

.tooltip-enter-from.right,
.tooltip-leave-to.right {
  transform: translate(-8px, -50%);
}

.tooltip-enter-to.top,
.tooltip-enter-to.bottom {
  transform: translateX(calc(-50% + var(--arrow-offset, 0px)));
}

.tooltip-enter-to.left,
.tooltip-enter-to.right {
  transform: translateY(-50%);
}
</style>
